import { User } from "oidc-client-ts";
import { config } from "../config";

// Subset of the Keycloak access token structure that the frontend needs for
// workspace routing and RBAC checks.
interface JwtRealmAccess {
  roles?: string[];
}

// Raw access-token claims used by the app. This stays close to the token field
// names so the decode step is easy to follow.
export interface AccessTokenClaims {
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  preferred_username?: string;
  groups?: string[];
  realm_access?: JwtRealmAccess;
  [key: string]: unknown;
}

// Frontend-friendly view of the auth data after claim names and group values
// have been normalized for the rest of the app.
export interface NormalizedAuthClaims {
  email?: string;
  givenName?: string;
  familyName?: string;
  fullName?: string;
  preferredUsername?: string;
  groups: string[];
  roles: string[];
  workspaceSlug?: string;
  isWorkspaceAdmin: boolean;
}

// Options shared by the async wait helpers that bridge the OIDC redirect
// callback timing during login.
interface WaitForClaimsOptions {
  intervalMs?: number;
  timeoutMs?: number;
  requireWorkspaceSlug?: boolean;
}

// OIDC user data is stored by oidc-client-ts under a deterministic key in
// sessionStorage. We reuse that key to read the current user outside React.
export function getOidcStorageKey(): string {
  return `oidc.user:${config.oidcAuthority}:${config.oidcClientId}`;
}

// Best-effort synchronous read of the stored OIDC user. Returns null if the
// user is not ready yet or if the stored payload cannot be parsed.
export function getStoredOidcUser(): User | null {
  const oidcStorage = sessionStorage.getItem(getOidcStorageKey());

  if (!oidcStorage) {
    return null;
  }

  try {
    return User.fromStorageString(oidcStorage);
  } catch (error) {
    console.error("Failed to parse stored OIDC user", error);
    return null;
  }
}

// Async variant of the stored-user read. This is used by API calls during the
// login callback race so they can wait briefly for the access token to appear.
export async function waitForStoredOidcUser(
  options: Omit<WaitForClaimsOptions, "requireWorkspaceSlug"> = {},
): Promise<User | null> {
  const {
    intervalMs = 100,
    timeoutMs = 4000,
  } = options;

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const user = getStoredOidcUser();

    if (user?.access_token) {
      return user;
    }

    await wait(intervalMs);
  }

  return null;
}

// JWT payloads are Base64URL encoded, so they need a small conversion before
// they can be parsed as JSON in the browser.
function decodeBase64Url(input: string): string {
  const normalizedInput = input.replace(/-/g, "+").replace(/_/g, "/");
  const paddingLength = (4 - (normalizedInput.length % 4)) % 4;
  const paddedInput = normalizedInput + "=".repeat(paddingLength);
  const binaryString = window.atob(paddedInput);

  const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// Decodes the payload section of a JWT token into a typed object. This helper
// is intentionally generic so the caller can decide which claim shape it needs.
export function decodeJwtPayload<T>(token?: string | null): T | null {
  if (!token) {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(parts[1])) as T;
  } catch (error) {
    console.error("Failed to decode JWT payload", error);
    return null;
  }
}

// Reads and decodes the current access token into the subset of claims the app
// cares about for workspace behavior.
export function getAccessTokenClaims(): AccessTokenClaims | null {
  const user = getStoredOidcUser();
  return decodeJwtPayload<AccessTokenClaims>(user?.access_token);
}

// Keycloak group values arrive with a leading slash, but the frontend uses the
// group as a workspace slug / subdomain, so it must be normalized first.
function normalizeGroup(group: string): string {
  return group.replace(/^\/+/, "").trim();
}

// Produces the normalized auth shape used across components and API helpers so
// the rest of the app does not depend on raw token claim names.
export function getNormalizedAuthClaims(): NormalizedAuthClaims {
  const claims = getAccessTokenClaims();
  const groups = (claims?.groups ?? [])
    .map(normalizeGroup)
    .filter((group) => group.length > 0);
  const roles = claims?.realm_access?.roles ?? [];
  const workspaceSlug = groups.length > 0 ? groups[0] : undefined;

  return {
    email: claims?.email,
    givenName: claims?.given_name,
    familyName: claims?.family_name,
    fullName: claims?.name,
    preferredUsername: claims?.preferred_username,
    groups,
    roles,
    workspaceSlug,
    isWorkspaceAdmin: roles.includes("workspace_admin"),
  };
}

// Small delay helper for the readiness polling functions below.
function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

// Waits briefly for the normalized claims to become available after login. This
// avoids early route-loader requests during the OIDC callback transition.
export async function waitForNormalizedAuthClaims(
  options: WaitForClaimsOptions = {},
): Promise<NormalizedAuthClaims | null> {
  const {
    intervalMs = 100,
    timeoutMs = 4000,
    requireWorkspaceSlug = false,
  } = options;

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const claims = getNormalizedAuthClaims();
    const hasStoredUser = getStoredOidcUser() !== null;

    if (hasStoredUser && (!requireWorkspaceSlug || Boolean(claims.workspaceSlug))) {
      return claims;
    }

    await wait(intervalMs);
  }

  return null;
}
